import React from 'react';
import { CalendarDays, TrendingUp, Percent, Clock, CreditCard, PiggyBank, AlertCircle, Target, Award } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета финансовой независимости
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Форматирование возраста для вывода
  const formatAge = (age) => {
    if (!age) return 'Не достигнуто';
    return `${age} ${pluralize(age, 'год', 'года', 'лет')}`;
  };

  // Определение статуса финансовой независимости
  const getFIStatus = () => {
    if (results.financialIndependenceAchieved) {
      return {
        status: 'success',
        message: `Вы достигнете финансовой независимости в ${formatAge(results.financialIndependenceAge)}`,
        icon: <Award className="w-5 h-5 text-green-500" />
      };
    } else {
      return {
        status: 'warning',
        message: 'Финансовая независимость не будет достигнута к планируемому возрасту',
        icon: <AlertCircle className="w-5 h-5 text-orange-500" />
      };
    }
  };

  const fiStatus = getFIStatus();

  return (
    <ToolResult title="Результаты расчета финансовой независимости">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Целевой капитал */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Target className="w-4 h-4"/>
            Целевой капитал
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.targetCapital)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Сумма, необходимая для получения пассивного дохода
          </p>
        </div>

        {/* Статус достижения финансовой независимости */}
        <div className={`rounded-lg p-5 border shadow-sm ${
          results.financialIndependenceAchieved 
            ? "bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-100 dark:border-green-800/50" 
            : "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 border-orange-100 dark:border-amber-800/50"
        }`}>
          <h3 className={`text-base font-medium mb-1.5 flex items-center gap-1.5 ${
            results.financialIndependenceAchieved 
              ? "text-green-700 dark:text-green-300" 
              : "text-orange-700 dark:text-orange-300"
          }`}>
            {fiStatus.icon}
            Статус финансовой независимости
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-xl font-bold ${
              results.financialIndependenceAchieved 
                ? "text-green-800 dark:text-green-200" 
                : "text-orange-800 dark:text-orange-200"
            }`} aria-live="polite">
              {fiStatus.message}
            </span>
          </div>
          <p className={`text-xs mt-1 ${
            results.financialIndependenceAchieved 
                ? "text-green-600 dark:text-green-400" 
                : "text-orange-600 dark:text-orange-400"
            }`}>
            {results.financialIndependenceAchieved 
                ? `Накоплений хватит для создания пассивного дохода в ${formatCurrency(results.projectedPassiveIncomeAtRetirement)}/мес`
                : `Требуется увеличить ежемесячные инвестиции до ${formatCurrency(results.monthlyAdditionsRequired)} (+${formatNumber((results.monthlyAdditionsRequired/results.monthlyAdditions - 1) * 100, 0)}%)`
            }
            </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Начальный капитал" 
          value={formatCurrency(results.initialCapital)} 
          color="indigo" 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Текущая ставка сбережений" 
          value={`${formatNumber(results.savingsRate * 100, 1)}% дохода`} 
          color="green" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        {!results.financialIndependenceAchieved && (
          <ResultItem 
            label="Требуемая ставка сбережений" 
            value={`${formatNumber(results.savingsRateRequired * 100, 1)}% дохода`} 
            color="orange" 
            icon={<Percent className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Ожидаемая реальная доходность" 
          value={`${formatNumber(results.realInvestmentReturn * 100, 2)}% годовых`} 
          color="blue" 
          icon={<TrendingUp className="w-4 h-4"/>}
        />
        
        {results.financialIndependenceAchieved ? (
          <ResultItem 
            label="Возраст достижения финансовой независимости" 
            value={formatAge(results.financialIndependenceAge)} 
            color="green"
            icon={<CalendarDays className="w-4 h-4"/>}
          />
        ) : (
          <ResultItem 
            label="Необходимые ежемесячные инвестиции" 
            value={formatCurrency(results.monthlyAdditionsRequired)} 
            color="orange"
            icon={<PiggyBank className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Накопления к моменту выхода на пенсию" 
          value={formatCurrency(results.totalSavingsAtRetirement)} 
        />
        
        <ResultItem 
          label="Пассивный доход после выхода на пенсию" 
          value={`${formatCurrency(results.projectedPassiveIncomeAtRetirement)}/мес`} 
          color="green" 
        />
        
        <ResultItem 
          label="Общий доход от инвестиций за период" 
          value={formatCurrency(results.totalInvestmentReturns)} 
          color="indigo" 
        />
        
        <ResultItem 
          label="Уплаченный налог на инвестиционный доход" 
          value={formatCurrency(results.totalTaxPaid)} 
          color="red" 
        />
        
        {results.financialIndependenceAchieved && (
          <ResultItem 
            label="Запас прочности" 
            value={`${formatNumber(results.retirementSafetyMargin, 1)}%`} 
            color="green" 
          />
        )}

        <ResultItem 
          label="Влияние инфляции на накопления" 
          value={`-${formatNumber(results.inflationImpact, 1)}%`} 
          color="orange" 
        />

        {/* Информация о расчете */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Clock className="w-5 h-5 text-indigo-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Информация о расчете</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Расчет финансовой независимости основан на принципе 4% (безопасная норма изъятия). 
                {results.inflationImpact > 10 && " Обратите внимание на значительное влияние инфляции на итоговый результат."}
                {!results.financialIndependenceAchieved && " В вашем случае, для достижения целей рекомендуется увеличить ежемесячные инвестиции или пересмотреть желаемый уровень пассивного дохода."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const pluralize = (number, one, few, many) => {
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return many;
  if (lastDigit === 1) return one;
  if (lastDigit >= 2 && lastDigit <= 4) return few;
  return many;
};

export default ResultsOverview;